import React, {useEffect, useState} from "react";
import Dialog from "@mui/material/Dialog";
import {Button, DialogActions, DialogContent, DialogTitle, Stack, TextField} from "@mui/material";
import {IFloorPlanBase} from "../../../common/interfaces/floor-plan-base";
import UploadIcon from "@mui/icons-material/Upload";
import {createToastNotification} from "../../../utilities/helpers";
import {convertFairFile} from "../../../api/conversion";
import {fetchFloorPlanBase, saveNewFloorPlanBase, updateFloorPlanBase} from "../../../api/floor-plan-base";
import AutorenewIcon from '@mui/icons-material/Autorenew';

export const FloorPlanBaseModal: React.FC<{
    base: IFloorPlanBase | null,
    open: boolean,
    onClose: () => void,
}> = ({base, open, onClose}) => {

    const [baseID, setBaseID] = useState<number | null>(null);
    const [baseName, setBaseName] = useState('');
    const [baseDescription, setBaseDescription] = useState('');
    const [baseSvg, setBaseSvg] = useState<File | null>(null);
    const [baseSource, setBaseSource] = useState<string | null>(null);

    const handleImageUpload = async (
        event: { target: { files: File[] } } | React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                let baseString = '';
                if (file.type === 'image/svg+xml') {
                    const reader = new FileReader();
                    baseString = await new Promise<string>((resolve, reject) => {
                        reader.onload = () => {
                            if (reader.result !== null) {
                                resolve(reader.result as string);
                            } else {
                                reject(new Error('Osnova prostornog plana nije priložena uspešno'));
                            }
                        }
                        reader.onerror = () => reject(reader.error);
                        reader.readAsText(file);
                    });
                } else {
                    baseString = await convertFairFile(file);
                }

                const blob = new Blob([baseString], {type: 'image/svg+xml'});
                const svgFile = new File([blob], "file.svg", {type: 'image/svg+xml'});
                const url = URL.createObjectURL(blob);
                setBaseSource(url);
                setBaseSvg(svgFile);
            } catch (error: any) {
                createToastNotification(
                    'error',
                    error?.response?.data?.message ?? 'Osnova prostornog plana nije priložena uspešno'
                );
            }
        } else {
            createToastNotification('error', '');
        }
    }


    const handleSaveBase = async () => {

        try{

            let data = {
                name:baseName,
                description:baseDescription,
                file:baseSvg
            };

            if(baseID){
                await updateFloorPlanBase(baseID, data)
            }else{
                await saveNewFloorPlanBase(data)
            }

            createToastNotification('success','Osnova prostornog plana je sačuvana uspešno');
            close()
        }catch(error:any){
            createToastNotification('error', error?.response?.data?.message);
        }
    }

    const close = ()=>{
        setBaseID(null);
        setBaseName('');
        setBaseSvg(null);
        setBaseSource(null);
        onClose()
    }

    const populateModalData = async () => {

        if(!base?.id){
            return;
        }

        try{
            const response = await fetchFloorPlanBase(base?.id)
            setBaseID(response.id)
            setBaseName(response.name)
            setBaseDescription(response.description)

            const file = await fetch(response.source);
            const blob = new Blob([await file.text()], {type: 'image/svg+xml'})
            const svgFile = new File([blob], "file.svg", {type: 'image/svg+xml'});
            const url = URL.createObjectURL(blob);
            setBaseSource(url);
            setBaseSvg(svgFile);

        }catch(error:any){
            createToastNotification(
                'error',
                error?.response?.data?.message
            );
        }

    }

    useEffect(() => {
        if(open){
            populateModalData()
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>{base?.id ? "Izmeni osnovu prostornog plana" : "Dodaj novu osnovu prostornog plana"}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    <TextField
                        label="Naziv"
                        name="name"
                        value={baseName}
                        onChange={(e) => setBaseName(e.target.value)}
                        fullWidth
                        required
                        InputLabelProps={{required: true}}
                    />
                    <TextField
                        label="Opis"
                        name="description"
                        value={baseDescription}
                        onChange={(e) => setBaseDescription(e.target.value)}
                        fullWidth
                    />
                    <>
                        {baseSource &&
                            <>
                                <h3>Pregled</h3>
                                <Stack
                                    sx={{
                                        width: 'sm',
                                        height: 'sm',
                                        overflow: "auto",
                                        border: "1px solid #ccc"
                                    }}
                                >
                                    <img
                                        src={baseSource}
                                        alt={'Prostorni plan nije priložen uspešno'}
                                        style={{maxWidth: "none", maxHeight: "none"}}
                                    />
                                </Stack>
                            </>
                        }
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={baseSvg ? <AutorenewIcon/> : <UploadIcon/>}
                        >
                            {baseSvg ? 'Zameni':'Priloži'} osnovu prostornog plana
                            <input
                                type="file"
                                hidden
                                accept=".svg, .dxf"
                                onChange={(e) => {
                                    handleImageUpload(e);
                                }}
                            />
                        </Button>
                    </>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={close} color="primary">Odustani</Button>
                <Button onClick={() => {
                    handleSaveBase()
                }} color="success" variant="contained">Sačuvaj</Button>
            </DialogActions>
        </Dialog>
    );
}
